import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  useToast,
  Icon,
  useColorModeValue,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { FaEnvelope, FaCheckCircle, FaRedo } from 'react-icons/fa';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';

const TIMER_SECONDS = 120;

const verifyEmailCode = async (uid: string, code: string) => {
  const functions = getFunctions();
  const callable = httpsCallable(functions, 'verifyEmailCode');
  const result = await callable({ uid, code });
  const data = result.data as { success: boolean; message?: string };
  if (!data.success) {
    throw new Error(data.message || 'Invalid code');
  }
};

const sendVerificationCode = async (email: string, uid: string) => {
  const functions = getFunctions();
  const callable = httpsCallable(functions, 'sendVerificationCode');
  const result = await callable({ email, uid });
  const data = result.data as { success: boolean; message?: string };
  if (!data.success) {
    throw new Error(data.message || 'Failed to send code');
  }
};



const VerifyCode: React.FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [code, setCode] = useState('');
  const [timer, setTimer] = useState(TIMER_SECONDS);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'pending' | 'verifying' | 'verified' | 'expired'>('pending');
  const [codeSent, setCodeSent] = useState(false);
  const [codeSendAttempted, setCodeSendAttempted] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [serverAttemptsChecked, setServerAttemptsChecked] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(() => getAuth().currentUser);
  const [authReady, setAuthReady] = useState(false);

  const bgColor = useColorModeValue('#09090b', '#09090b');
  const cardBg = useColorModeValue('#191919', '#191919');
  const textColor = useColorModeValue('white', 'white');

  // Check if code was already sent in this session
  const getCodeSentFlag = () => {
    if (!currentUser || !email) return false;
    const flag = localStorage.getItem(`verificationCodeSent_${currentUser.uid}_${email}`);
    if (!flag) return false;
    
    try {
      const { timestamp } = JSON.parse(flag);
      const now = Date.now();
      const timeDiff = now - timestamp;
      
      // If more than 2 minutes have passed, consider it expired
      if (timeDiff > TIMER_SECONDS * 1000) {
        localStorage.removeItem(`verificationCodeSent_${currentUser.uid}_${email}`);
        return false;
      }
      
      return true;
    } catch (error) {
      // If there's an error parsing the flag, clear it
      localStorage.removeItem(`verificationCodeSent_${currentUser.uid}_${email}`);
      return false;
    }
  };

  // Set code sent flag
  const setCodeSentFlag = () => {
    if (!currentUser || !email) return;
    const flag = {
      timestamp: Date.now(),
      email: email
    };
    localStorage.setItem(`verificationCodeSent_${currentUser.uid}_${email}`, JSON.stringify(flag));
  };

  // Clear code sent flag
  const clearCodeSentFlag = () => {
    if (!currentUser || !email) return;
    localStorage.removeItem(`verificationCodeSent_${currentUser.uid}_${email}`);
  };

  // Get attempts from localStorage
  const getAttemptsFromStorage = () => {
    if (!currentUser || !email) return { attempts: 0, maxAttempts: 3 };
    const attemptsData = localStorage.getItem(`verificationAttempts_${currentUser.uid}_${email}`);
    if (!attemptsData) return { attempts: 0, maxAttempts: 3 };
    
    try {
      const data = JSON.parse(attemptsData);
      const now = Date.now();
      
      // If more than 2 minutes have passed, reset attempts
      if (now - data.timestamp > TIMER_SECONDS * 1000) {
        localStorage.removeItem(`verificationAttempts_${currentUser.uid}_${email}`);
        return { attempts: 0, maxAttempts: 3 };
      }
      
      return { attempts: data.attempts, maxAttempts: 3 };
    } catch (error) {
      return { attempts: 0, maxAttempts: 3 };
    }
  };

  // Set attempts in localStorage
  const setAttemptsInStorage = (attempts: number) => {
    if (!currentUser || !email) return;
    const data = {
      attempts: attempts,
      timestamp: Date.now()
    };
    localStorage.setItem(`verificationAttempts_${currentUser.uid}_${email}`, JSON.stringify(data));
  };

  // Clear attempts from localStorage
  const clearAttemptsFromStorage = () => {
    if (!currentUser || !email) return;
    localStorage.removeItem(`verificationAttempts_${currentUser.uid}_${email}`);
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(t => t - 1), 1000);
      return () => clearInterval(interval);
    } else if (status === 'pending') {
      setStatus('expired');
    }
  }, [timer, status]);



  useEffect(() => {
    if (authReady && currentUser && email && !serverAttemptsChecked) {
      // Check localStorage for attempts first
      const attemptsData = getAttemptsFromStorage();
      const remainingAttempts = Math.max(0, attemptsData.maxAttempts - attemptsData.attempts);
      setAttemptsRemaining(remainingAttempts);
      setAttemptsUsed(attemptsData.attempts);
      
      // If no attempts remaining, set status to expired
      if (remainingAttempts === 0) {
        setStatus('expired');
        setError('Maximum attempts reached. Please request a new code.');
      }
      
      setServerAttemptsChecked(true);
    }
  }, [authReady, currentUser, email, serverAttemptsChecked]);

  useEffect(() => {
    if (authReady && currentUser && email && serverAttemptsChecked && !codeSendAttempted) {
      // Check if code was already sent in this session
      const wasCodeSent = getCodeSentFlag();
      setCodeSent(wasCodeSent);
      
      if (!wasCodeSent) {
        // Only auto-send if no code was previously sent
        setCodeSendAttempted(true);
        sendCode();
      } else {
        // If code was already sent, start timer from where it left off
        setCodeSendAttempted(true);
        const flag = localStorage.getItem(`verificationCodeSent_${currentUser.uid}_${email}`);
        if (flag) {
          try {
            const { timestamp } = JSON.parse(flag);
            const now = Date.now();
            const timeDiff = now - timestamp;
            const remainingTime = Math.max(0, TIMER_SECONDS - Math.floor(timeDiff / 1000));
            setTimer(remainingTime);
            
            if (remainingTime === 0) {
              setStatus('expired');
            }
          } catch (error) {
            // If there's an error parsing the flag, clear it and send a new code
            clearCodeSentFlag();
            sendCode();
          }
        }
      }
    }
  }, [authReady, currentUser, email, serverAttemptsChecked, codeSendAttempted]);

  const sendCode = async () => {
    if (!currentUser) return;

    setResendLoading(true);
    setError('');
    setTimer(TIMER_SECONDS);
    setCode('');
    setStatus('pending');
    setAttemptsRemaining(3); // Reset attempts counter
    setAttemptsUsed(0); // Reset attempts used counter
    clearAttemptsFromStorage(); // Clear attempts from localStorage
    setCodeSendAttempted(true); // Mark that we've attempted to send a code


    try {
      await sendVerificationCode(email, currentUser.uid);
      setCodeSentFlag();
      setCodeSent(true);
      toast({
        title: 'Verification code sent!',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (err: any) {
      setError(err.message || 'Failed to send code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setCode(value);
    
    // Auto-verify when 3 digits are entered
    if (value.length === 3) {
      handleVerify(value);
    }
  };

  const handleVerify = async (codeToVerify?: string) => {
    if (!currentUser) return;
    
    const codeToUse = codeToVerify || code;
    if (codeToUse.length !== 3) return;

    setStatus('verifying');
    setError('');
    setLoading(true);
    
    // Increment attempts used counter
    const newAttemptsUsed = attemptsUsed + 1;
    setAttemptsUsed(newAttemptsUsed);
    setAttemptsInStorage(newAttemptsUsed); // Store in localStorage
    
    // Update attempts remaining immediately
    const newAttemptsRemaining = Math.max(0, 3 - newAttemptsUsed);
    setAttemptsRemaining(newAttemptsRemaining);

    try {
      await verifyEmailCode(currentUser.uid, codeToUse);

      // Update user profile in Firestore
      const firestore = getFirestore();
      const userDoc = doc(firestore, 'users', currentUser.uid);
      await updateDoc(userDoc, {
        emailVerified: true,
        updatedAt: serverTimestamp()
      });

      // Clear the code sent flag on successful verification
      clearCodeSentFlag();
      clearAttemptsFromStorage(); // Clear attempts on success

      // Create 30-day trial immediately after email verification (only if they don't already have one)
      await createDefaultTrial(currentUser.uid);

      setStatus('verified');
      toast({
        title: 'Email verified successfully!',
        status: 'success',
        duration: 2000,
        isClosable: true
      });

      // Redirect to paywall page with registration data
      setTimeout(() => {
        const registrationPlanId = localStorage.getItem('registrationPlanId');
        const registrationPlanName = localStorage.getItem('registrationPlanName');
        const registrationOriginalAmount = localStorage.getItem('registrationOriginalAmount');
        const registrationPromoCode = localStorage.getItem('registrationPromoCode');
        const registrationPromoCodeId = localStorage.getItem('registrationPromoCodeId');
        const registrationDiscountPercentage = localStorage.getItem('registrationDiscountPercentage');

        const params = new URLSearchParams();
        if (registrationPlanId) params.set('planId', registrationPlanId);
        if (registrationPlanName) params.set('planName', registrationPlanName);
        if (registrationOriginalAmount) params.set('originalAmount', registrationOriginalAmount);
        params.set('newRegistration', 'true');
        if (registrationPromoCode) params.set('promoCode', registrationPromoCode);
        if (registrationPromoCodeId) params.set('promoCodeId', registrationPromoCodeId);
        if (registrationDiscountPercentage) params.set('discountPercentage', registrationDiscountPercentage);

        navigate(`/paywall?${params.toString()}`);
      }, 1000);

    } catch (err: any) {

      
      if (err.code === 'deadline-exceeded') {
        setStatus('expired');
        setError('Code expired. Please request a new code.');
        clearCodeSentFlag(); // Clear flag when code expires
        clearAttemptsFromStorage(); // Clear attempts when code expires
      } else if (err.code === 'permission-denied') {
        // Check if it's a maximum attempts error
        if (err.message && err.message.includes('Maximum attempts reached')) {
          setStatus('expired');
          setError('Maximum attempts reached. Please request a new code.');
          clearCodeSentFlag(); // Clear flag when max attempts reached
          setAttemptsRemaining(0); // Set attempts to 0
          setAttemptsInStorage(3); // Store max attempts reached
        } else {
          // Show error with current attempts remaining
          setError(`Incorrect code. ${newAttemptsRemaining} attempts remaining.`);
          setCode(''); // Clear input for retry
          inputRef.current?.focus();
          
          // If this was the last attempt, disable input
          if (newAttemptsRemaining === 0) {
            setStatus('expired');
            setAttemptsInStorage(3); // Store max attempts reached
          }
          
          // Fallback: If we've used 3 or more attempts, force disable
          if (newAttemptsUsed >= 3) {
            setAttemptsRemaining(0);
            setStatus('expired');
            setError('Maximum attempts reached. Please request a new code.');
            setAttemptsInStorage(3); // Store max attempts reached
          }
        }
      } else {
        setError('Verification failed. Please try again.');
      }

      setStatus('pending');
    } finally {
      setLoading(false);
    }
  };

  // Function to create default trial after email verification
  const createDefaultTrial = async (userId: string) => {
    try {
      // Get plan info from localStorage - use original (non-discounted) plan
      const originalPlanId = localStorage.getItem('registrationOriginalPlanId') || localStorage.getItem('registrationPlanId') || '';
      const originalPlanName = localStorage.getItem('registrationOriginalPlanName') || localStorage.getItem('registrationPlanName') || '';
      
      // Remove "(DISCOUNTED)" if it exists
      const planName = originalPlanName.replace(' (DISCOUNTED)', '');
      
      // If no plan was selected, use a default individual plan
      const finalPlanId = originalPlanId || 'individual';
      const finalPlanName = planName || 'Individual Plan';
      
      // Create trial subscription without card verification (NO promo code)
      const { doc, setDoc, serverTimestamp, getDoc } = await import('firebase/firestore');
      const { firestore } = await import('../../config/firebase');
      
             // Check if user already has a trial
       const userDoc = await getDoc(doc(firestore, 'users', userId));
       if (userDoc.exists() && userDoc.data()?.trial) {
         return;
       }
      
      await setDoc(doc(firestore, 'users', userId), {
        trial: {
          status: 'active',
          started: serverTimestamp(),
          trial_ends: new Date(Date.now() + 2592e6), // 30 days (exactly like deployed)
          skippedVerification: true,
          planId: finalPlanId,
          planName: finalPlanName, // This will be the non-discounted plan name
          appliedPromoCode: null // NO promo code for default trial
        },
        updatedAt: serverTimestamp()
             }, { merge: true });
         } catch (error) {
       // Don't throw error - we don't want to break the email verification flow
       // The trial can be created later if needed
     }
  };

  if (!authReady) {
    return (
      <Box maxW="sm" mx="auto" mt={16} p={8} bg={cardBg} borderRadius="lg" boxShadow="lg">
        <Text color={textColor} textAlign="center">Loading...</Text>
      </Box>
    );
  }

  const isVerified = status === 'verified';
  const canResend = status === 'expired' || (codeSent && timer === 0);

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box w="full" maxW="md" bg={cardBg} borderRadius="lg" p={8} border="1px solid" borderColor="gray.700">
        <VStack spacing={6} textAlign="center">
          {/* Header */}
          <VStack spacing={4}>
            <Icon
              as={isVerified ? FaCheckCircle : FaEnvelope}
              boxSize={12}
              color={isVerified ? 'green.400' : 'blue.400'}
            />
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              {isVerified ? 'Email Verified!' : 'Verify Your Email'}
            </Text>
            <Text color="gray.300">
              {isVerified
                ? 'Redirecting to subscription setup...'
                : 'Enter the 3-digit code sent to your email.'
              }
            </Text>
          </VStack>

          {/* Email Display */}
          <Box w="full" p={4} bg="gray.800" borderRadius="md">
            <Text color="gray.400" fontSize="sm">Email Address</Text>
            <Text color="white" fontWeight="medium">{email}</Text>
          </Box>

          {/* Verification Form */}
          {!isVerified && (
            <VStack spacing={4} w="full">
              {/* Code Input */}
              <HStack justify="center" w="full">
                <Input
                  ref={inputRef}
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="_ _ _"
                  maxLength={3}
                  size="lg"
                  fontSize="2xl"
                  textAlign="center"
                  bg="#191919"
                  color="white"
                  borderColor={error ? "red.400" : "gray.600"}
                  borderWidth="2px"
                  borderRadius="md"
                  w="120px"
                  isDisabled={status === 'verifying' || status === 'expired' || attemptsRemaining === 0 || attemptsUsed >= 3}
                  autoFocus
                />
              </HStack>

                             {/* Timer and Attempts */}
               <VStack spacing={1}>
                 <Text color="gray.400" fontSize="sm">
                   {timer > 0 ? `Expires in ${timer}s` : 'Code expired'}
                 </Text>
                                   {attemptsRemaining === 1 && (
                    <Text color="orange.400" fontSize="sm">
                      1 attempt remaining
                    </Text>
                  )}
               </VStack>

              {/* Error Message */}
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {/* Resend Button */}
              {canResend && (
                <Button
                  leftIcon={<FaRedo />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={sendCode}
                  isLoading={resendLoading}
                  w="full"
                  borderRadius="2px"
                >
                  Resend Code
                </Button>
              )}




            </VStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default VerifyCode; 